import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SignIn } from "@phosphor-icons/react";
import CDLogo from "@/components/CDLogo";

const BG = "https://images.pexels.com/photos/7479032/pexels-photo-7479032.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      const from = loc.state?.from;
      const to = from
        ? `${from.pathname || "/"}${from.search || ""}${from.hash || ""}`
        : "/";
      nav(to, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-zinc-100 text-zinc-900" data-testid="login-page">
      <div className="hidden lg:block lg:col-span-3 relative grain overflow-hidden">
        <img src={BG} alt="Industrial warehouse" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-zinc-950/65" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <CDLogo className="w-14 h-14 shrink-0" />
            <div>
              <div className="font-heading font-black text-xl leading-tight">Cabinet Depot</div>
              <div className="text-[11px] uppercase tracking-[0.3em] text-zinc-300 font-semibold">Desktop · Web</div>
            </div>
          </div>
          <div className="max-w-lg">
            <div className="text-[10px] uppercase tracking-[0.4em] text-red-300 font-semibold mb-3">View · Print · Manage</div>
            <h1 className="font-heading font-black text-4xl sm:text-5xl leading-[1.05] tracking-tight">
              Your warehouse, in the browser.
            </h1>
            <p className="mt-5 text-zinc-300 text-base leading-relaxed">
              The desktop companion to your Cabinet Depot floor app. View every order, print Avery 5163
              labels with QR codes, and manage your team — synced with Base44 in real time.
            </p>
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 font-semibold">
            © Cabinet Depot · Desktop v1.0
          </div>
        </div>
      </div>

      <div className="lg:col-span-2 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <CDLogo className="w-12 h-12 shrink-0" />
            <div className="font-heading font-black text-lg">Cabinet Depot Desktop</div>
          </div>

          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-semibold">Sign in</div>
          <h2 className="font-heading font-black text-3xl mt-1 mb-1">Welcome back</h2>
          <p className="text-sm text-zinc-500 mb-8">Use the same credentials as the mobile app.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-600">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="mt-1.5 h-12"
                placeholder="you@cabinetdepot.com"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-600">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="mt-1.5 h-12"
                data-testid="login-password-input"
              />
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm bg-red-50 text-red-700 border border-red-200 rounded-md px-3 py-2">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-12 font-semibold tracking-wide"
              style={{ background: "#C8102E" }}
            >
              <SignIn size={18} weight="bold" className="mr-2" />
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <p className="mt-8 text-xs text-zinc-500 leading-relaxed" data-testid="login-help">
            This is the desktop view of the Cabinet Depot Workflow. It connects directly to your
            mobile app&apos;s backend — every order, item, and scan is shared in real time.
          </p>
        </div>
      </div>
    </div>
  );
}
